import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { call, signInHref } from "./api";
import type { Favorite, FavoriteKind, League, SavedItem, SearchEntry, User } from "./api";
import { deviceSearchRows } from "./deviceSearches";

/**
 * Who's signed in, and their favourites, for every page: the header's
 * buttons, the star on a team or fighter, the search box's history. One
 * provider at the root (main.tsx), so the /auth/me call happens once per page
 * load whichever sport's desk is showing.
 */
interface Account {
  /** "loading" until /auth/me has answered; the header shows no account buttons until then. */
  status: "loading" | "ready";
  user: User | null;
  signIn: (email: string, password: string) => Promise<User>;
  signUp: (displayName: string, email: string, password: string) => Promise<User>;
  /** With the ID token Google's button hands the page (DSPRT-82). */
  signInWithGoogle: (credential: string) => Promise<User>;
  /** From a reset link (DSPRT-81): sets the password and signs this device in. */
  resetPassword: (token: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  rename: (displayName: string) => Promise<void>;
  /** Null until loaded (or when signed out). */
  favorites: Favorite[] | null;
  isFavorite: (league: League, kind: FavoriteKind, ref: string) => boolean;
  /** Adds or removes. Signed out, it goes to the sign-in page instead and comes back here. */
  toggleFavorite: (item: SavedItem) => Promise<void>;
  /** Mutes or unmutes one favorite's alerts (DSPRT-651); it stays a favorite. Put back if the server says no. */
  setFavoriteAlerts: (id: number, on: boolean) => Promise<void>;
  /** A result opened from search. Fire and forget; nothing is recorded signed out. */
  /** `listed`: picked from the Recent or Popular list rather than searched for. */
  recordSearch: (item: SavedItem & { query: string; listed?: boolean }) => void;
}

const AccountContext = React.createContext<Account | null>(null);

export function useAccount(): Account {
  const a = useContext(AccountContext);
  if (!a) throw new Error("useAccount outside AccountProvider");
  return a;
}

const same = (f: SavedItem, league: League, kind: FavoriteKind, ref: string) =>
  f.league === league && f.kind === kind && f.ref === ref;

export function AccountProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<"loading" | "ready">("loading");
  const [user, setUser] = useState<User | null>(null);
  const [favorites, setFavorites] = useState<Favorite[] | null>(null);

  useEffect(() => {
    call<{ user: User | null }>("GET", "/auth/me")
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))   // API down: the site still works, signed out
      .finally(() => setStatus("ready"));
  }, []);

  useEffect(() => {
    if (!user) {
      setFavorites(null);
      return;
    }
    let live = true;
    call<Favorite[]>("GET", "/me/favorites").then((f) => { if (live) setFavorites(f); }).catch(() => {});
    return () => { live = false; };
  }, [user?.id]);   // eslint-disable-line react-hooks/exhaustive-deps -- a rename mustn't refetch

  const signIn = useCallback(async (email: string, password: string) => {
    const r = await call<{ user: User }>("POST", "/auth/signin", { email, password });
    setUser(r.user);
    return r.user;
  }, []);

  const signUp = useCallback(async (displayName: string, email: string, password: string) => {
    const r = await call<{ user: User }>("POST", "/auth/signup", { displayName, email, password });
    setUser(r.user);
    return r.user;
  }, []);

  const signInWithGoogle = useCallback(async (credential: string) => {
    const r = await call<{ user: User }>("POST", "/auth/google", { credential });
    setUser(r.user);
    return r.user;
  }, []);

  const resetPassword = useCallback(async (token: string, password: string) => {
    const r = await call<{ user: User }>("POST", "/auth/reset", { token, password });
    setUser(r.user);
    return r.user;
  }, []);

  const signOut = useCallback(async () => {
    await call("POST", "/auth/signout").catch(() => {});
    setUser(null);
    // The account pages are about the account that just left.
    if (/^\/(account|admin)(\/|$)/.test(location.pathname)) location.assign("/");
  }, []);

  const rename = useCallback(async (displayName: string) => {
    const r = await call<{ user: User }>("PATCH", "/me", { displayName });
    setUser(r.user);
  }, []);

  const isFavorite = useCallback(
    (league: League, kind: FavoriteKind, ref: string) => !!favorites?.some((f) => same(f, league, kind, ref)),
    [favorites],
  );

  const toggleFavorite = useCallback(async (item: SavedItem) => {
    if (!user) {
      location.assign(signInHref());
      return;
    }
    const existing = favorites?.find((f) => same(f, item.league, item.kind, item.ref));
    if (existing) {
      // Optimistic, and put back if the server says no.
      setFavorites((fs) => fs?.filter((f) => f.id !== existing.id) ?? fs);
      try {
        await call("DELETE", `/me/favorites/${existing.id}`);
      } catch (e) {
        setFavorites((fs) => (fs ? [...fs, existing] : fs));
        throw e;
      }
    } else {
      const saved = await call<Favorite>("POST", "/me/favorites", item);
      setFavorites((fs) => [...(fs ?? []).filter((f) => f.id !== saved.id), saved]);
    }
  }, [user, favorites]);

  // One write at a time per favorite: two quick taps sent together can land in
  // either order, leaving the server on the opposite of the switch (DSPRT-651 QA).
  const favWrites = useRef(new Map<number, Promise<unknown>>());
  const setFavoriteAlerts = useCallback(async (id: number, on: boolean) => {
    const flip = (v: boolean) => setFavorites((fs) => fs?.map((f) => (f.id === id ? { ...f, alerts: v } : f)) ?? fs);
    flip(on);
    const write = (favWrites.current.get(id) ?? Promise.resolve()).then(() => call("PATCH", `/me/favorites/${id}`, { alerts: on }));
    favWrites.current.set(id, write.catch(() => {}));
    try {
      await write;
    } catch (e) {
      flip(!on);
      throw e;
    }
  }, []);

  const recordSearch = useCallback(({ listed, ...item }: SavedItem & { query: string; listed?: boolean }) => {
    // Counted for the search box's "Popular" list (DSPRT-297): what was
    // opened, never by whom, signed in or not. Only what was searched for:
    // a pick from Recent or Popular itself would have the list feed itself.
    if (!listed) call("POST", "/search-open", { league: item.league, kind: item.kind, ref: item.ref }).catch(() => {});
    // Signed out, the history stays on this device.
    if (!user) saveDeviceSearch(item);
    else call("POST", "/me/searches", item).catch(() => {});
  }, [user]);

  const value = useMemo<Account>(() => ({
    status, user, signIn, signUp, signInWithGoogle, resetPassword, signOut, rename, favorites, isFavorite,
    toggleFavorite, setFavoriteAlerts, recordSearch,
  }), [status, user, signIn, signUp, signInWithGoogle, resetPassword, signOut, rename, favorites, isFavorite,
       toggleFavorite, setFavoriteAlerts, recordSearch]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

/**
 * The signed-in visitor's favourite NBA teams, by code (DSPRT-305): their
 * games lead Scores and the score strip. Empty signed out, and until the
 * favourites have loaded. A stable set while the list doesn't change.
 */
export function useFavoriteTeams(): ReadonlySet<string> {
  const { favorites } = useAccount();
  return useMemo(
    () => new Set((favorites ?? []).filter((f) => f.league === "nba" && f.kind === "team").map((f) => f.ref)),
    [favorites],
  );
}

/** Whether either side of a game is one of them. */
export function isFavoriteGame(favs: ReadonlySet<string>, g: { home: { abbr: string }; away: { abbr: string } }): boolean {
  return favs.size > 0 && (favs.has(g.home.abbr) || favs.has(g.away.abbr));
}

/* A signed-out visitor's recent searches (DSPRT-297): the last few results
   opened, kept on this device only. Signing in switches to the account's own
   history; these stay where they are for the next signed-out visit. */
const DEVICE_SEARCHES_KEY = "statsummit:searches";
const DEVICE_SEARCHES_KEEP = 8;

function readDeviceSearches(): SearchEntry[] {
  try {
    return deviceSearchRows(JSON.parse(localStorage.getItem(DEVICE_SEARCHES_KEY) ?? "[]"));
  } catch {
    return [];
  }
}

function writeDeviceSearches(rows: SearchEntry[]): void {
  try { localStorage.setItem(DEVICE_SEARCHES_KEY, JSON.stringify(rows)); } catch { /* private mode: nothing kept */ }
}

function saveDeviceSearch(item: SavedItem & { query: string }): void {
  const same = (e: SearchEntry) => e.league === item.league && e.kind === item.kind && e.ref === item.ref;
  const { league, kind, ref, label, sub, imageUrl, query } = item;
  // Opening the same thing again moves it to the top, as the account's history does.
  writeDeviceSearches([
    { id: Date.now(), league, kind, ref, label, sub, imageUrl, query, searchedAt: new Date().toISOString() },
    ...readDeviceSearches().filter((e) => !same(e)),
  ].slice(0, DEVICE_SEARCHES_KEEP));
}

/**
 * Recent searches, for the search box to show before anything is typed: the
 * account's when signed in, this device's when not. Loaded each time the box
 * opens (`active`), so a search made on the other sport's page is there too.
 */
export function useSearchHistory(active: boolean): {
  entries: SearchEntry[];
  remove: (id: number) => void;
  clear: () => void;
} {
  const { user, status } = useAccount();
  const [entries, setEntries] = useState<SearchEntry[]>([]);
  const signedIn = !!user;

  useEffect(() => {
    // Nothing until /auth/me has answered: a signed-in visitor would otherwise
    // see the device's list for a moment, then their own.
    if (!active || status !== "ready") {
      setEntries([]);
      return;
    }
    if (!user) {
      setEntries(readDeviceSearches());
      return;
    }
    let live = true;
    call<SearchEntry[]>("GET", "/me/searches?limit=20").then((e) => { if (live) setEntries(e); }).catch(() => {});
    return () => { live = false; };
  }, [active, status, user?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const remove = useCallback((id: number) => {
    setEntries((es) => es.filter((e) => e.id !== id));
    if (signedIn) call("DELETE", `/me/searches/${id}`).catch(() => {});
    else writeDeviceSearches(readDeviceSearches().filter((e) => e.id !== id));
  }, [signedIn]);

  const clear = useCallback(() => {
    setEntries([]);
    if (signedIn) call("DELETE", "/me/searches").catch(() => {});
    else writeDeviceSearches([]);
  }, [signedIn]);

  return { entries, remove, clear };
}
